// Manufacturer-org in-app order creation form (OI-4).
//
// Drives two submission shapes in one component, switched by the kind toggle:
//   - Inventory Build  -> source = in_app_inventory, kind = inventory
//   - Customer Order   -> source = in_app_customer,  kind = customer
//
// Both submit through the existing createInAppIntake service (OI-2) and land
// the resulting row in this org's Pending Review queue. The hook (which owns
// the createBusy / createError surface) refreshes the list + flips the tab
// back to Pending Review on success.

import { useEffect, useState } from "react";

import { SectionCard } from "@/components/common/SectionCard";
import { listManufacturingPlants, listManufacturingRecipes } from "@/lib/manufacturingClient";
import type { MfgPlant } from "@/lib/manufacturing/types";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types/recipes";
import type { CreateIntakeBody, OpsOrderIntakeSalePath } from "@/lib/orderIntakeClient";

import { buildInAppCreateIntakeBody } from "./manufacturingOrderIntakeCreateBuild";
import { IntakeEndCustomerContactField } from "./IntakeEndCustomerContactField";
import { IntakePlantPacketUrlFields } from "./IntakePlantPacketUrlFields";
import {
  ghostBtnClass,
  inputClass,
  primaryBtnClass,
} from "./ManufacturingOrderIntakeShared";

type FormKind = "inventory" | "customer";

const SALE_PATHS: ReadonlyArray<{ id: OpsOrderIntakeSalePath; label: string }> = [
  { id: "cash", label: "Cash sale" },
  { id: "finance", label: "Financed" },
  { id: "rto", label: "Rent-to-own" },
  // inventory_no_sale_yet is intentionally excluded: it's the implicit
  // sale_path for inventory builds and is auto-applied by the factory
  // when an inventory-kind intake is accepted (see resolveSalePath()).
];

export type ManufacturingOrderIntakeCreateFormProps = {
  busy: boolean;
  error: string | null;
  onSubmit: (body: CreateIntakeBody) => Promise<boolean>;
  onCancel: () => void;
};

export function ManufacturingOrderIntakeCreateForm({
  busy,
  error,
  onSubmit,
  onCancel,
}: ManufacturingOrderIntakeCreateFormProps) {
  const [kind, setKind] = useState<FormKind>("inventory");
  const [plantId, setPlantId] = useState("");
  const [sellerLocationId, setSellerLocationId] = useState("");
  const [modelId, setModelId] = useState("");
  const [salePath, setSalePath] = useState<OpsOrderIntakeSalePath | "">("");
  const [endCustomerContactId, setEndCustomerContactId] = useState("");
  const [endCustomerLabel, setEndCustomerLabel] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [inventoryHolderOrgId, setInventoryHolderOrgId] = useState("");
  const [notes, setNotes] = useState("");
  const [saleAmountUsd, setSaleAmountUsd] = useState("");
  const [orderFormUrl, setOrderFormUrl] = useState("");
  const [floorPlanUrl, setFloorPlanUrl] = useState("");

  const [plants, setPlants] = useState<MfgPlant[]>([]);
  const [recipes, setRecipes] = useState<MfgRecipeSummary[]>([]);
  const [catalogBusy, setCatalogBusy] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setCatalogBusy(true);
      setCatalogError(null);
      const [p, r] = await Promise.all([
        listManufacturingPlants(),
        listManufacturingRecipes(),
      ]);
      if (cancelled) return;
      setCatalogBusy(false);
      if (!p.ok) {
        setCatalogError(p.error);
        return;
      }
      const plantList = Array.isArray(p.data.plants) ? p.data.plants : [];
      const activePlants = plantList.filter((x) => x.is_active);
      setPlants(activePlants);
      const initialPlant =
        activePlants.find((x) => x.is_default)?.id ?? activePlants[0]?.id ?? "";
      setPlantId((prev) => (prev === "" ? initialPlant : prev));

      if (!r.ok) {
        setRecipes([]);
        setCatalogError((prev) => prev ?? r.error);
        return;
      }
      const recipeList = Array.isArray(r.data.recipes) ? r.data.recipes : [];
      setRecipes(recipeList.filter((x) => x.is_active));
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  function buildBody() {
    return buildInAppCreateIntakeBody({
      kind,
      plantId,
      sellerLocationId,
      modelId,
      salePath,
      endCustomerContactId,
      inventoryHolderOrgId,
      notes,
      saleAmountUsd,
      orderFormUrl,
      floorPlanUrl,
      customerName,
      customerEmail,
      customerPhone,
    });
  }

  async function handleSubmit() {
    setFormError(null);
    const built = buildBody();
    if ("error" in built) {
      setFormError(built.error);
      return;
    }
    const ok = await onSubmit(built);
    if (ok) {
      setNotes("");
      setSellerLocationId("");
      setEndCustomerContactId("");
      setEndCustomerLabel("");
      setCustomerName("");
      setCustomerEmail("");
      setCustomerPhone("");
      setInventoryHolderOrgId("");
      setSalePath("");
      setModelId("");
      setSaleAmountUsd("");
      setOrderFormUrl("");
      setFloorPlanUrl("");
    }
  }

  return (
    <SectionCard
      title="Create new order"
      description="Submits an Order Intake row in your org's queue. Accept later to materialize a deal + unit."
    >
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <KindToggle current={kind} value="inventory" onClick={() => setKind("inventory")}>
            Inventory build
          </KindToggle>
          <KindToggle current={kind} value="customer" onClick={() => setKind("customer")}>
            Customer order
          </KindToggle>
        </div>

        {catalogBusy ? (
          <div className="text-xs text-gray-500 dark:text-zinc-400">
            Loading plants + models…
          </div>
        ) : null}
        {catalogError ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
            {catalogError}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FieldLabel label="Plant" required>
            <select
              className={inputClass}
              value={plantId}
              onChange={(e) => setPlantId(e.target.value)}
              disabled={busy || catalogBusy}
            >
              <option value="" disabled>
                {plants.length ? "Select a plant" : "No active plants"}
              </option>
              {plants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.plant_code} · {p.name}
                </option>
              ))}
            </select>
          </FieldLabel>

          <FieldLabel label="Model (optional)">
            <select
              className={inputClass}
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              disabled={busy || catalogBusy}
            >
              <option value="">Unspecified</option>
              {recipes.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.code} · {r.name}
                </option>
              ))}
            </select>
          </FieldLabel>

          <FieldLabel label="Seller location (optional UUID)">
            <input
              className={inputClass}
              value={sellerLocationId}
              onChange={(e) => setSellerLocationId(e.target.value)}
              placeholder="ops_seller_locations.id"
              disabled={busy}
            />
          </FieldLabel>

          {kind === "customer" ? (
            <>
              <FieldLabel label="Sale path" required>
                <select
                  className={inputClass}
                  value={salePath}
                  onChange={(e) => setSalePath(e.target.value as OpsOrderIntakeSalePath | "")}
                  disabled={busy}
                >
                  <option value="" disabled>
                    Select sale path
                  </option>
                  {SALE_PATHS.map((sp) => (
                    <option key={sp.id} value={sp.id}>
                      {sp.label}
                    </option>
                  ))}
                </select>
              </FieldLabel>

              <FieldLabel label="End customer" required>
                <IntakeEndCustomerContactField
                  selectedLabel={endCustomerLabel}
                  disabled={busy}
                  onPick={(hit) => {
                    setEndCustomerContactId(hit.id);
                    setEndCustomerLabel(hit.name || hit.id.slice(0, 8));
                    setCustomerName(hit.name);
                    setCustomerEmail(hit.email);
                    setCustomerPhone(hit.phone);
                  }}
                  onClear={() => {
                    setEndCustomerContactId("");
                    setEndCustomerLabel("");
                    setCustomerName("");
                    setCustomerEmail("");
                    setCustomerPhone("");
                  }}
                />
              </FieldLabel>

              <FieldLabel label="Inventory holder org (optional UUID)">
                <input
                  className={inputClass}
                  value={inventoryHolderOrgId}
                  onChange={(e) => setInventoryHolderOrgId(e.target.value)}
                  placeholder="organizations.id (default: this org)"
                  disabled={busy}
                />
              </FieldLabel>
              <FieldLabel label="Sale amount (optional)">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  step="0.01"
                  value={saleAmountUsd}
                  onChange={(e) => setSaleAmountUsd(e.target.value)}
                  placeholder="12000"
                  disabled={busy}
                />
              </FieldLabel>
            </>
          ) : null}
        </div>

        <IntakePlantPacketUrlFields
          orderFormUrl={orderFormUrl}
          floorPlanUrl={floorPlanUrl}
          onOrderFormUrl={setOrderFormUrl}
          onFloorPlanUrl={setFloorPlanUrl}
          disabled={busy}
        />

        <FieldLabel label="Notes (optional)">
          <textarea
            className={`${inputClass} min-h-[80px]`}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything the reviewer should know — colour, options, target ship date, etc."
            disabled={busy}
          />
        </FieldLabel>

        {formError ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
            {formError}
          </div>
        ) : null}
        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
            {error}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={primaryBtnClass}
            disabled={busy || catalogBusy || !plantId}
            onClick={() => {
              void handleSubmit();
            }}
          >
            {busy ? "Submitting…" : "Submit to intake queue"}
          </button>
          <button
            type="button"
            className={ghostBtnClass}
            disabled={busy}
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </SectionCard>
  );
}

function KindToggle({
  current,
  value,
  onClick,
  children,
}: {
  current: FormKind;
  value: FormKind;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "rounded-full px-3 py-1.5 text-sm font-medium transition-colors " +
        (active
          ? "bg-indigo-600 text-white"
          : "bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700")
      }
    >
      {children}
    </button>
  );
}

function FieldLabel({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-xs text-gray-600 dark:text-zinc-300">
      <span className="block">
        {label}
        {required ? <span className="text-red-600"> *</span> : null}
      </span>
      <span className="mt-1 block">{children}</span>
    </label>
  );
}
